/**
 * ViewModeToggle — the floating pill that switches `FileContentView` between MD Preview, Branded Doc
 * and Edit (Track 3 §5.1 component tree, CR §3.4).
 *
 * ── WHY NOT `role="tablist"` ────────────────────────────────────────────────────────────────────
 * The obvious choice is the shared `ui/tabs` primitive, which is what the deleted cards used. It is
 * wrong here for two reasons. First, this control sits *inside* a tab panel owned by `FileTabStrip`,
 * and nesting a tablist inside a tabpanel makes arrow-key navigation ambiguous — the user cannot
 * tell which tablist their arrow keys will drive. Second, "Branded Doc" is conditionally absent
 * (only two of the five artifacts have a branded pipeline), and a tablist whose membership changes
 * per file reads as a different control each time.
 *
 * A `role="group"` of `aria-pressed` toggles avoids both: predictable Tab-then-Enter operation, no
 * competing keyboard model, and a stable accessible name regardless of how many modes are offered.
 */

import { Eye, FileText, Pencil } from 'lucide-react';

import { cn } from '@/lib/utils';

export type FileViewMode = 'md' | 'branded' | 'edit';

export interface ViewModeToggleProps {
  mode: FileViewMode;
  onChange: (mode: FileViewMode) => void;
  /** Omits the Branded Doc option for the three artifacts with no branded pipeline. */
  showBranded: boolean;
  className?: string;
}

const MODE_META: Record<FileViewMode, { label: string; Icon: typeof Eye }> = {
  md: { label: 'MD Preview', Icon: Eye },
  branded: { label: 'Branded Doc', Icon: FileText },
  edit: { label: 'Edit', Icon: Pencil },
};

export function ViewModeToggle({
  mode,
  onChange,
  showBranded,
  className,
}: ViewModeToggleProps): JSX.Element {
  const modes: FileViewMode[] = showBranded ? ['md', 'branded', 'edit'] : ['md', 'edit'];

  return (
    <div
      role="group"
      aria-label="View mode"
      className={cn(
        // Floating pill: sticky so it stays reachable while a long document scrolls beneath it.
        'sticky top-2 z-10 mx-auto flex w-fit items-center gap-1 rounded-badge border border-outlines bg-white/95 p-1 shadow-lg backdrop-blur',
        className,
      )}
    >
      {modes.map((m) => {
        const { label, Icon } = MODE_META[m];
        const isActive = mode === m;
        return (
          <button
            key={m}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(m)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-badge px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              isActive
                ? 'bg-primary-subtle text-primary-700 ring-1 ring-primary'
                : 'text-text-secondary hover:bg-subtle-bg hover:text-text-primary',
            )}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default ViewModeToggle;
